import { useState, useCallback, useMemo } from "react"
import { api } from "../../../lib/api/client"
import { throttle } from "../../../lib/utils"
import type { CollectionName, CollectionSearchResponse } from "./types"

interface UseAsyncSearchOptions<T> {
  // Data source
  collection?: CollectionName
  options?: T[]
  isLoading?: boolean

  // Config
  filter?: Record<string, any>
  sort?: Record<string, 1 | -1>
  limit?: number
  getItemId?: (item: T) => string
  getItemLabel?: (item: T) => string
  addNull?: boolean
}

interface UseAsyncSearchResult<T> {
  items: T[]
  isLoading: boolean
  error: Error | null
  search: (query: string) => void
  loadInitial: () => void
}

/**
 * Hook for async search with support for both API collection search
 * and client-side filtering of static options.
 */
export function useAsyncSearch<T>({
  collection,
  options,
  isLoading: externalLoading = false,
  filter,
  sort,
  limit = 20,
  getItemId = (item: any) => item._id,
  getItemLabel = (item: any) => item.name || item.title || item.display_name || "",
  addNull,
}: UseAsyncSearchOptions<T>): UseAsyncSearchResult<T> {
  const [items, setItems] = useState<T[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [searchQuery, setSearchQuery] = useState("")

  const isStaticMode = !collection && !!options

  /**
   * For static mode: filter options client-side
   */
  const filteredStaticItems = useMemo(() => {
    if (!isStaticMode || !options) return []

    const filtered = searchQuery
      ? options.filter((item) =>
          getItemLabel(item).toLowerCase().includes(searchQuery.toLowerCase())
        )
      : options

    return filtered.slice(0, limit)
  }, [isStaticMode, options, searchQuery, getItemLabel, limit])

  /**
   * Perform API search for collection mode
   */
  const performSearch = useCallback(
    async (query: string) => {
      if (isStaticMode) {
        setSearchQuery(query)
        return
      }

      if (!collection) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const searchParams = {
          searchString: query,
          filter: filter || {},
          limit,
          ...(sort && { sort }),
        }

        const apiMethod = (api as any)[collection]?.search
        if (!apiMethod) {
          throw new Error(`No search method found for collection: ${collection}`)
        }

        const response: CollectionSearchResponse = await apiMethod(searchParams)

        if (!response.success && !response.data) {
          setError(new Error("Search failed"))
          setItems([])
          return
        }

        const documents = response.data?.documents || response.data || []
        setItems(documents)
      } catch (err) {
        setError(err as Error)
        setItems([])
      } finally {
        setIsLoading(false)
      }
    },
    [collection, filter, limit, sort, isStaticMode]
  )

  /**
   * Throttled search - runs after 300ms or after 3 calls
   */
  const throttledSearch = useMemo(
    () => throttle(performSearch, 300, 3),
    [performSearch]
  )

  /**
   * Search function to be called on input change
   */
  const search = useCallback(
    (query: string) => {
      if (isStaticMode) {
        setSearchQuery(query)
      } else {
        throttledSearch(query)
      }
    },
    [throttledSearch, isStaticMode]
  )

  /**
   * Load initial options (empty search for collection mode) - always fetches fresh results
   */
  const loadInitial = useCallback(() => {
    if (!isStaticMode) {
      performSearch("")
    }
  }, [performSearch, isStaticMode])

  // Use static items when in static mode, otherwise use fetched items
  const resultItems = isStaticMode ? filteredStaticItems : items
  const resultLoading = isStaticMode ? externalLoading : isLoading

  return {
    items: resultItems,
    isLoading: resultLoading,
    error,
    search,
    loadInitial,
  }
}
